import React, { useState, useMemo } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { Coupon } from '../../types';

const AdminCoupons: React.FC = () => {
  const { coupons, createCoupon, updateCoupon, deleteCoupon, toggleCouponStatus } = useAdmin();

  // StatusManagement
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'expired'>('all');
  const [filterType, setFilterType] = useState<'' | 'percentage' | 'fixed'>('');

  // Form data
  const [formData, setFormData] = useState<Omit<Coupon, 'id' | 'created_at' | 'updated_at'>>({
    code: '',
    name: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_amount: 0,
    usage_per_user: 1,
    is_active: true
  });

  // Filteringed Coupon List
  const filteredCoupons = useMemo(() => {
    let result = coupons;
    const now = new Date();

    // Search Filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        c => c.code.toLowerCase().includes(search) || c.name.toLowerCase().includes(search)
      );
    }

    // Status Filter
    if (filterStatus !== 'all') {
      result = result.filter(c => {
        if (filterStatus === 'active') {
          return c.is_active && (!c.end_date || new Date(c.end_date) > now);
        } else if (filterStatus === 'inactive') {
          return !c.is_active;
        } else if (filterStatus === 'expired') {
          return c.end_date && new Date(c.end_date) <= now;
        }
        return true;
      });
    }

    // Type Filter
    if (filterType) {
      result = result.filter(c => c.discount_type === filterType);
    }

    return result;
  }, [coupons, searchTerm, filterStatus, filterType]);

  // Calculate statistics
  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: coupons.length,
      active: coupons.filter(c => c.is_active && (!c.end_date || new Date(c.end_date) > now)).length,
      inactive: coupons.filter(c => !c.is_active).length,
      expired: coupons.filter(c => c.end_date && new Date(c.end_date) <= now).length,
      totalUsage: coupons.reduce((sum, c) => sum + (c.usage_count || 0), 0)
    };
  }, [coupons]);

  // Helper functions
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unlimited';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (endDate?: string) => {
    if (!endDate) return '';
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return 'Expired';
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires tomorrow';
    return `${days} days remaining`;
  };

  const isExpired = (endDate?: string) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  // Open modal
  const handleOpenModal = (coupon?: Coupon) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        name: coupon.name,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_amount: coupon.min_amount,
        max_discount: coupon.max_discount,
        usage_limit: coupon.usage_limit,
        usage_per_user: coupon.usage_per_user,
        start_date: coupon.start_date,
        end_date: coupon.end_date,
        is_active: coupon.is_active
      });
    } else {
      setEditingCoupon(null);
      setFormData({
        code: '',
        name: '',
        discount_type: 'percentage',
        discount_value: 0,
        min_amount: 0,
        usage_per_user: 1,
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  // Coupon Save
  const handleSaveCoupon = async () => {
    try {
      if (!formData.code || !formData.name) {
        alert('Coupon code and name are required.');
        return;
      }

      if (editingCoupon) {
        await updateCoupon(editingCoupon.id, formData);
        alert('Coupon has been updated.');
      } else {
        await createCoupon(formData);
        alert('Coupon has been created.');
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Coupon Save Failed:', error);
      alert('Failed to save coupon.');
    }
  };

  // Coupon Delete
  const handleDeleteCoupon = async (couponId: string) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await deleteCoupon(couponId);
        alert('Coupon has been deleted.');
      } catch (error) {
        console.error('Coupon Delete Failed:', error);
        alert('Failed to delete coupon.');
      }
    }
  };

  // Toggle coupon status
  const handleToggleStatus = async (couponId: string, currentStatus: boolean) => {
    try {
      await toggleCouponStatus(couponId, currentStatus);
    } catch (error) {
      console.error('Failed to change coupon status:', error);
      alert('Failed to change coupon status.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Coupon Management</h1>
          <p className="mt-2 text-sm text-neutral-700">
            {coupons.length} coupon(s) registered in total.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-forest-400 hover:bg-forest-500"
          >
            + Create New Coupon
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="🎫" label="All Coupons" value={stats.total} color="blue" />
        <StatCard icon="✅" label="Active Coupons" value={stats.active} color="green" />
        <StatCard icon="⏰" label="Expired Coupons" value={stats.expired} color="red" />
        <StatCard icon="📊" label="Total Usage Count" value={stats.totalUsage} color="purple" />
      </div>

      {/* Filter/Search Area */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search by coupon code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
            >
              <option value="">All Types</option>
              <option value="percentage">Percentage Discount (%)</option>
              <option value="fixed">Fixed Discount ($)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Coupon Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Coupon Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Discount Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Conditions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Usage Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Validity Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Management
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredCoupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-neutral-500">
                    No search results found.
                  </td>
                </tr>
              ) : (
                filteredCoupons.map(coupon => (
                  <tr key={coupon.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-neutral-900">{coupon.code}</div>
                      <div className="text-xs text-neutral-500">{coupon.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {coupon.discount_type === 'percentage' ? (
                        <span className="text-sm font-semibold text-green-600">
                          {(coupon.discount_value * 100).toFixed(0)}% Discount
                        </span>
                      ) : (
                        <span className="text-sm font-semibold text-forest-400">
                          ${coupon.discount_value.toLocaleString()} Discount
                        </span>
                      )}
                      {coupon.max_discount && (
                        <div className="text-xs text-neutral-500">Max ${coupon.max_discount.toLocaleString()}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700">
                      {coupon.min_amount > 0 && (
                        <div>Minimum {coupon.min_amount.toLocaleString()}USD</div>
                      )}
                      <div className="text-xs text-neutral-500">{coupon.usage_per_user} per user</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-700">
                      <div>
                        Usage: <span className="font-semibold">{coupon.usage_count || 0}</span>
                        {coupon.usage_limit && ` / ${coupon.usage_limit} uses`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm ${
                          isExpired(coupon.end_date) ? 'text-red-600' : 'text-neutral-700'
                        }`}
                      >
                        {formatDate(coupon.end_date)}
                      </div>
                      {coupon.end_date && (
                        <div className="text-xs text-neutral-500">{getDaysRemaining(coupon.end_date)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(coupon.id, coupon.is_active)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          coupon.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'
                        }`}
                      >
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => handleOpenModal(coupon)}
                        className="text-forest-400 hover:text-forest-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCoupon(coupon.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-neutral-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            ></div>

            {/* Modal body */}
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-neutral-900 mb-4">
                  {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </h3>

                {/* Form fields */}
                <div className="space-y-4">
                  {/* Coupon Code */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Coupon Code *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g. SUMMER2024"
                      className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                    />
                  </div>

                  {/* Coupon Name */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Coupon Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Summer Season Special Discount"
                      className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                    />
                  </div>

                  {/* Discount type and value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Discount Type *</label>
                      <select
                        value={formData.discount_type}
                        onChange={(e) =>
                          setFormData({ ...formData, discount_type: e.target.value as 'percentage' | 'fixed' })
                        }
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      >
                        <option value="percentage">Percentage Discount (%)</option>
                        <option value="fixed">Fixed Discount ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">
                        {formData.discount_type === 'percentage' ? 'Discount Rate (%)' : 'Discount Amount ($)'}
                      </label>
                      <input
                        type="number"
                        value={formData.discount_value}
                        onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                        placeholder={formData.discount_type === 'percentage' ? '10' : '5'}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      />
                    </div>
                  </div>

                  {/* Minimum Order Amount */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Minimum Order Amount (USD)</label>
                    <input
                      type="number"
                      value={formData.min_amount}
                      onChange={(e) => setFormData({ ...formData, min_amount: parseInt(e.target.value) || 0 })}
                      placeholder="0 = no limit"
                      className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                    />
                  </div>

                  {/* Max discount amount (for percentage discount) */}
                  {formData.discount_type === 'percentage' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Max Discount Amount ($)</label>
                      <input
                        type="number"
                        value={formData.max_discount || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, max_discount: parseInt(e.target.value) || undefined })
                        }
                        placeholder="Leave empty for unlimited"
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      />
                    </div>
                  )}

                  {/* Usage limits */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Uses Per User</label>
                      <input
                        type="number"
                        value={formData.usage_per_user}
                        onChange={(e) => setFormData({ ...formData, usage_per_user: parseInt(e.target.value) || 1 })}
                        placeholder="1"
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Total Available Uses</label>
                      <input
                        type="number"
                        value={formData.usage_limit || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, usage_limit: parseInt(e.target.value) || undefined })
                        }
                        placeholder="Leave empty for unlimited"
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      />
                    </div>
                  </div>

                  {/* Validity period */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Start Date</label>
                      <input
                        type="datetime-local"
                        value={formData.start_date || ''}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">End Date</label>
                      <input
                        type="datetime-local"
                        value={formData.end_date || ''}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                      />
                    </div>
                  </div>

                  {/* Activation status */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-forest-400 focus:ring-forest-500 border-neutral-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-neutral-700">Activate Coupon</label>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="bg-neutral-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSaveCoupon}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-forest-400 text-base font-medium text-white hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {editingCoupon ? 'Save' : 'Create'}
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Statistics card component
const StatCard: React.FC<{
  icon: string;
  label: string;
  value: number;
  color: 'blue' | 'green' | 'red' | 'purple';
}> = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-forest-50 text-forest-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    purple: 'bg-purple-50 text-purple-700'
  };

  return (
    <div className={`${colorClasses[color]} rounded-lg p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <span className="text-4xl">{icon}</span>
      </div>
    </div>
  );
};

export default AdminCoupons;
